import React, { useEffect, useRef, useState } from 'react';
import {
    AlertTriangle,
    Bell, BellRing, BookOpen, ClipboardCheck,
    CalendarDays,
    CheckCircle2,
    ChevronLeft,
    ChevronRight,
    Edit,
    Lock,
    Loader2,
    Megaphone,
    Pencil, RefreshCw, Save, Send, Trash2, Users, X,
} from 'lucide-react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { format, isValid } from 'date-fns';
import { toast } from 'sonner';
import api from '@/lib/axiosInstance';
import Helper from '@/lib/helper';
import type { RootState } from '@/lib/redux/store';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DatePicker } from '@/components/DatePicker';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';

// ─── Types ───────────────────────────────────────────────────────────────────

type Channels = {
    notifyInApp?: number | boolean;
    notifyEmail?: number | boolean;
    notifyWebPush?: number | boolean;
};

type ReminderRepeat = 'none' | 'daily' | 'weekly' | 'monthly';

type Task = Channels & {
    id: number | string;
    title: string;
    description?: string | null;
    source: string;
    dueDate?: string | null;
    reminderTime?: string | null;
    priority?: string;
    repeat?: ReminderRepeat;
    repeatDays?: string | null;
    repeatUntilOff?: boolean;
};

const WEEKDAYS = [
    { key: 'mon', label: 'Mon' },
    { key: 'tue', label: 'Tue' },
    { key: 'wed', label: 'Wed' },
    { key: 'thu', label: 'Thu' },
    { key: 'fri', label: 'Fri' },
    { key: 'sat', label: 'Sat' },
    { key: 'sun', label: 'Sun' },
];
const parseRepeatDays = (value?: string | null): string[] => String(value || '').split(',').map(day => day.trim()).filter(Boolean);

type TrainingAttachment = {
    id?: number;
    name: string;
    url?: string | null;
    objectKey?: string | null;
};

type TrainingRank = {
    id: number;
    name: string;
    level: number;
    pointsRequired?: number | null;
    medal?: string | null;
    color?: string | null;
};

const RANK_COLOR_PALETTE = [
    '#10b981', '#0ea5e9', '#f59e0b', '#f43f5e',
    '#8b5cf6', '#14b8a6', '#f97316', '#ec4899',
];
const rankColor = (rank: { color?: string | null; level?: number; id?: number }) => {
    if (rank.color && /^#[0-9a-fA-F]{3,8}$/.test(rank.color)) return rank.color;
    const seed = (Number(rank.level) || Number(rank.id) || 1) - 1;
    return RANK_COLOR_PALETTE[((seed % RANK_COLOR_PALETTE.length) + RANK_COLOR_PALETTE.length) % RANK_COLOR_PALETTE.length];
};

const RankChip = ({ rank }: { rank: TrainingRank }) => {
    const color = rankColor(rank);
    return (
        <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium"
            style={{ backgroundColor: `${color}1a`, color, border: `1px solid ${color}40` }}
        >
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
            {rank.name}
        </span>
    );
};

const CATEGORY_META: Record<string, { label: string; className: string }> = {
    videos: { label: 'Videos', className: 'bg-sky-50 text-sky-700 border-sky-200' },
    physical: { label: 'Physical', className: 'bg-amber-50 text-amber-700 border-amber-200' },
    general_questions: { label: 'General Questions', className: 'bg-rose-50 text-rose-700 border-rose-200' },
};
const categoryChip = (raw: string | undefined | null) => {
    const key = (raw || '').toLowerCase().replace(/\s+/g, '_');
    return CATEGORY_META[key] || { label: raw || '—', className: 'bg-gray-50 text-gray-700 border-gray-200' };
};

type ScheduledTrainingLike = {
    scheduledDate?: string | null;
    endDate?: string | null;
    startTime?: string | null;
    startsAt?: string | null;
    endsAt?: string | null;
    isAvailable?: boolean;
};

const trainingStartDate = (item: ScheduledTrainingLike) => {
    if (item.startsAt) {
        const parsed = new Date(item.startsAt);
        if (isValid(parsed)) return parsed;
    }
    if (!item.scheduledDate || !item.startTime) return null;
    const [year, month, day] = item.scheduledDate.slice(0, 10).split('-').map(Number);
    const [hour = 0, minute = 0, second = 0] = item.startTime.split(':').map(Number);
    const parsed = new Date(year, month - 1, day, hour, minute, second);
    return isValid(parsed) ? parsed : null;
};

const isTrainingTimeAvailable = (item: ScheduledTrainingLike, now = new Date()) => {
    if (item.isAvailable === false) return false;
    const startsAt = trainingStartDate(item);
    if (startsAt) return now >= startsAt;
    return item.isAvailable !== false;
};

const formatTrainingStart = (item: ScheduledTrainingLike) => {
    const startsAt = trainingStartDate(item);
    return startsAt ? format(startsAt, 'MMM d, yyyy h:mm a') : '—';
};

// Self-paced stage training surfaces how many days a stage has gone uncompleted.
// Use colour and explicit copy instead of strikethrough so "due" never reads as deleted.
const procrastinationTier = (days: number) => {
    if (days >= 7) return { text: 'text-red-600', badge: 'bg-red-50 text-red-700 ring-1 ring-red-200' };
    if (days >= 3) return { text: 'text-orange-600', badge: 'bg-orange-50 text-orange-700 ring-1 ring-orange-200' };
    return { text: 'text-amber-600', badge: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' };
};

type TrainingItem = Channels & {
    id: number;
    title: string;
    category: string;
    trainingType?: string | null;
    trainingMode?: 'scheduled' | 'stage' | string | null;
    videoTitle?: string | null;
    videoUrl?: string | null;
    videoObjectKey?: string | null;
    thumbnailUrl?: string | null;
    scheduledDate?: string | null;
    endDate?: string | null;
    startTime?: string | null;
    endTime?: string | null;
    startsAt?: string | null;
    endsAt?: string | null;
    availableFrom?: string | null;
    isAvailable?: boolean;
    lockReason?: string | null;
    procrastinationDays?: number | null;
    completedDate?: string | null;
    unlockOffsetValue?: number | string | null;
    unlockOffsetUnit?: string | null;
    createdAt?: string | null;
    attachments?: TrainingAttachment[];
    trainingRanks?: TrainingRank[];
    minTrainingRank?: TrainingRank | null;
    hasQuiz?: boolean;
    checklistText: string;
    progress?: {
        maxWatchedSeconds?: number;
        lastPositionSeconds?: number;
        durationSeconds?: number;
        watchedAt?: string | null;
        completedAt?: string | null;
    } | null;
};

type ManualSection = {
    id: number;
    title: string;
    category: string;
    content?: string | null;
    files?: {
        id: number;
        fileKind: 'pdf' | 'image';
        name: string;
        objectKey?: string;
        url?: string;
    }[];
};

type Announcement = Channels & {
    id: number;
    title: string;
    message?: string | null;
    description?: string | null;
    imageObjectKey?: string | null;
    imageUrl?: string | null;
    scheduledAt?: string | null;
    createdAt?: string | null;
};

type Lead = {
    id: number;
    sessionId: number;
    name: string;
    project?: string | null;
    email?: string | null;
    contact?: string | null;
    status: 'available' | 'claimed' | 'closed' | 'surrendered' | string;
    claimedBy?: number | null;
    claimedByOther?: boolean;
    claimedAt?: string | null;
    claimExpiresAt?: string | null;
    followedUpAt?: string | null;
    surrenderedAt?: string | null;
    surrenderReason?: string | null;
    temperature?: 'cold' | 'warm' | 'hot' | string | null;
    notes?: string | null;
    remarks?: LeadRemark[];
    reminderAt?: string | null;
};

type LeadRemark = {
    id: number;
    leadId: number;
    agentId?: number | null;
    remark: string;
    createdAt: string;
};

type LeadSession = {
    id: number;
    title: string;
    description?: string | null;
    project?: string | null;
    status: string;
    isPool?: number | boolean | null;
    computedStatus?: string;
    liveAt?: string | null;
    closeAt?: string | null;
    leads?: Lead[];
};

type DashboardData = {
    announcements?: Announcement[];
    manualSections: ManualSection[];
    taskCenter: {
        all: Task[];
        custom: Task[];
        trainingReminders: Task[];
    };
    trainingItems: TrainingItem[];
    leadSessions?: LeadSession[];
    currentRank?: TrainingRank | null;
};

// ─── Constants ────────────────────────────────────────────────────────────────

const todayIso = () => format(new Date(), 'yyyy-MM-dd');
const s3BaseUrl = `${import.meta.env.VITE_AWS_S3 || ''}`.replace(/\/$/, '');

const emptyReminderForm = {
    title: '',
    description: '',
    dueDate: todayIso(),
    reminderTime: '09:00',
    priority: 'normal',
    repeat: 'none' as ReminderRepeat,
    repeatDays: '' as string,
    repeatUntilOff: false,
    notifyInApp: 1,
    notifyEmail: 0,
    notifyWebPush: 0,
};

const tabs = [
    { value: 'announcements', label: 'Announcements', icon: Megaphone },
    { value: 'lead-market', label: 'Lead Market', icon: Users },
    { value: 'my-leads', label: 'My Leads', icon: Users },
    { value: 'training', label: 'Training', icon: ClipboardCheck },
    { value: 'reminders', label: 'Reminders', icon: Bell },
    { value: 'manual', label: 'Manual', icon: BookOpen },
];

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const cx = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(' ');

// ─── Helpers ─────────────────────────────────────────────────────────────────

const mediaUrl = (path?: string | null) => {
    if (!path) return '';
    if (/^(https?:|blob:)/.test(path)) return path;
    return `${s3BaseUrl}/${path.replace(/^\//, '')}`;
};

const formatReminderDate = (value?: string | null) => {
    if (!value) return '-';
    const date = value.includes('T') || value.includes(' ')
        ? new Date(value)
        : new Date(`${value}T00:00:00`);
    return isValid(date)
        ? format(date, value.includes(':') ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy')
        : value;
};

const formatDateTimeLabel = (value?: string | null) => {
    if (!value) return '-';
    const date = new Date(value);
    return isValid(date) ? format(date, 'dd/MM/yyyy h:mm a') : value;
};

const formatReminderTime = (value?: string | null) => {
    if (!value) return '';
    return value.slice(0, 5);
};

const parsePickerDate = (value?: string | null) => {
    if (!value) return undefined;
    const date = new Date(`${value}T00:00:00`);
    return isValid(date) ? date : undefined;
};

const toIsoDate = (date?: Date) => (date ? format(date, 'yyyy-MM-dd') : '');

const isoDate = (date: Date) => format(date, 'yyyy-MM-dd');

const dateKeyFromValue = (value?: string | null) => {
    if (!value) return '';
    // Take the calendar-date portion as authored (no timezone conversion) so a date like
    // "2026-06-26" or "2026-06-26T00:00:00+08:00" never shifts to the 25th in another timezone.
    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
    const date = new Date(value);
    return isValid(date) ? isoDate(date) : '';
};


type StageSegment = { item: TrainingItem; isStart: boolean; isEnd: boolean; completed: boolean; overdueDays: number };
type StageStatus = 'completed' | 'active' | 'locked';
type StageAnchor = { dateKey: string; sourceDateKey: string; completedKey: string; status: StageStatus; projected: boolean; overdueDays: number };

const stageOffsetDays = (item: TrainingItem) => {
    const v = Number(item.unlockOffsetValue || 0);
    if (!Number.isFinite(v) || v <= 0) return 0;
    return String(item.unlockOffsetUnit || 'days') === 'weeks' ? v * 7 : v;
};

const addDaysToKey = (key: string, days: number) => {
    const d = new Date(`${key}T00:00:00`);
    if (!isValid(d)) return key;
    d.setDate(d.getDate() + days);
    return isoDate(d);
};

// Work out where each stage sits on the calendar. Unlocked/completed stages use their real
// availableFrom; locked stages are *projected* by chaining the unlock delay off the previous
// stage, so the agent can still see them (greyed/locked) and roughly when they're coming.
const computeStageAnchors = (items: TrainingItem[], now = new Date()): Map<number, StageAnchor> => {
    const map = new Map<number, StageAnchor>();
    const todayKey = isoDate(now);
    let prevRefKey = '';
    for (const item of items) {
        if (item.trainingMode !== 'stage') continue;
        const offset = stageOffsetDays(item);
        const completedKey = item.completedDate || (item.progress?.completedAt ? dateKeyFromValue(item.progress.completedAt) : '');
        const realStart = dateKeyFromValue(item.availableFrom);
        let dateKey: string;
        let status: StageStatus;
        let projected: boolean;
        if (realStart) {
            dateKey = realStart;
            projected = false;
            status = completedKey ? 'completed' : 'active';
        } else {
            // Locked → project from the previous stage's reference date + this stage's delay.
            dateKey = addDaysToKey(prevRefKey || todayKey, offset);
            projected = true;
            status = 'locked';
        }
        const overdueDays = status === 'active' ? Math.max(0, Number(item.procrastinationDays || 0)) : 0;
        const sourceDateKey = dateKey;
        const displayDateKey = status === 'active' && overdueDays > 0 ? todayKey : dateKey;
        map.set(item.id, { dateKey: displayDateKey, sourceDateKey, completedKey, status, projected, overdueDays });
        // The next stage projects off this stage's completion (real) or its start (projected/active).
        prevRefKey = completedKey || sourceDateKey;
    }
    return map;
};

// Stages are day-based; show only the calendar date (no time, no timezone conversion).
const formatStageDate = (value?: string | null) => {
    const key = dateKeyFromValue(value);
    if (!key) return '';
    const d = new Date(`${key}T00:00:00`);
    return isValid(d) ? format(d, 'MMM d, yyyy') : '';
};

const trainingTimeLabel = (item: TrainingItem) => {
    if (item.trainingMode === 'stage') return item.availableFrom ? `Unlocks ${formatStageDate(item.availableFrom)}` : 'Self-paced';
    const start = item.startTime?.slice(0, 5);
    const end = item.endTime?.slice(0, 5);
    const endDate = dateKeyFromValue(item.endDate);
    if (start && endDate && end) return `${start} - ${endDate} ${end}`;
    return start ? `${start} onwards` : '';
};

const trainingDisplayState = (item: TrainingItem, _index: number, _items: TrainingItem[], now = new Date()) => {
    const isStage = item.trainingMode === 'stage';
    const completed = Boolean(item.progress?.completedAt);
    const watched = Boolean(item.progress?.watchedAt);
    const started = Boolean(item.progress?.maxWatchedSeconds) || watched || completed;
    // Stage = gated by the previous stage (server-resolved isAvailable). Scheduled = gated only by its date/time.
    const timeUnlocked = isStage ? item.isAvailable !== false : isTrainingTimeAvailable(item, now);
    const canWatch = isStage ? item.isAvailable !== false : timeUnlocked;
    const locked = !canWatch;
    const procrastinationDays = isStage && !completed ? Math.max(0, Number(item.procrastinationDays || 0)) : 0;
    const procrastinationStyle = procrastinationDays > 0 ? procrastinationTier(procrastinationDays) : null;
    const status = completed ? 'Completed'
        : !timeUnlocked ? 'Scheduled'
        : watched ? 'Ready'
        : started ? 'In progress'
        : 'Not started';
    const statusVariant: 'default' | 'secondary' | 'outline' = completed ? 'default' : (watched || started) ? 'secondary' : 'outline';
    const lockedMessage = isStage
        ? (item.lockReason || 'Complete the previous stage first.')
        : `Training starts on ${formatTrainingStart(item)}.`;
    return {
        isStage,
        completed,
        watched,
        started,
        timeUnlocked,
        canWatch,
        locked,
        procrastinationDays,
        procrastinationStyle,
        status,
        statusVariant,
        lockedMessage,
    };
};

// ─── Main Component ───────────────────────────────────────────────────────────

const AgentHandbook = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedTab = searchParams.get('tab') || 'announcements';
    const activeTab = requestedTab === 'leads' ? 'lead-market' : requestedTab;
    const agent = useSelector((state: RootState) => state.auth.agent);
    const [data, setData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [claimingLeadId, setClaimingLeadId] = useState<number | null>(null);
    const [reminderForm, setReminderForm] = useState<any>(emptyReminderForm);
    const [notificationOpen, setNotificationOpen] = useState(false);
    const [addReminderOpen, setAddReminderOpen] = useState(false);
    const [editingReminder, setEditingReminder] = useState<Task | null>(null);
    const [deleteReminderTarget, setDeleteReminderTarget] = useState<Task | null>(null);
    const [webPushSupported, setWebPushSupported] = useState(false);
    const [webPushGranted, setWebPushGranted] = useState(false);
    const [manageLead, setManageLead] = useState<Lead | null>(null);
    const [newRemark, setNewRemark] = useState('');
    const [addingRemark, setAddingRemark] = useState(false);
    const [reminderDays, setReminderDays] = useState('1');
    const [settingReminder, setSettingReminder] = useState(false);
    const [leadSearch, setLeadSearch] = useState('');
    const [leadProjectFilter, setLeadProjectFilter] = useState('all');
    const [reminderSelectMode, setReminderSelectMode] = useState(false);
    const [selectedReminderLeadIds, setSelectedReminderLeadIds] = useState<number[]>([]);
    const [bulkReminderDays, setBulkReminderDays] = useState('1');
    const [bulkReminderBusy, setBulkReminderBusy] = useState(false);
    const [dayDialogOpen, setDayDialogOpen] = useState(false);
    const [nowTick, setNowTick] = useState(() => new Date());
    const [trainingCursor, setTrainingCursor] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    });
    const [selectedTrainingDate, setSelectedTrainingDate] = useState(() => todayIso());
    const navigate = useNavigate();

    useEffect(() => {
        const supported = 'Notification' in window && 'serviceWorker' in navigator;
        setWebPushSupported(supported);
        if (supported) setWebPushGranted(Notification.permission === 'granted');
    }, []);

    useEffect(() => {
        const timer = window.setInterval(() => setNowTick(new Date()), 30000);
        return () => window.clearInterval(timer);
    }, []);

    useEffect(() => {
        if (activeTab !== 'training') return;
        const now = new Date();
        setTrainingCursor(new Date(now.getFullYear(), now.getMonth(), 1));
        setSelectedTrainingDate(isoDate(now));
    }, [activeTab]);

    const requestWebPush = async () => {
        if (!webPushSupported) return toast.error('Web push is not supported in this browser.');
        const result = await Notification.requestPermission();
        setWebPushGranted(result === 'granted');
        if (result === 'granted') toast.success('Web push notifications enabled!');
        else toast.error('Web push permission denied.');
    };

    const loadDashboard = async () => {
        try {
            setLoading(true);
            const response = await api.get('/agent-handbook');
            if (!response.data.status) throw new Error(response.data.error);
            setData(response.data);
        } catch (err: any) {
            toast.error(Helper.errorHandler(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadDashboard(); }, []);

    const submit = async (url: string, payload: Record<string, any>, success: string) => {
        try {
            setSaving(true);
            const response = await api.post(url, payload, { headers: { 'Content-Type': 'application/json' } });
            if (!response.data.status) throw new Error(response.data.error);
            toast.success(success);
            await loadDashboard();
            return true;
        } catch (err: any) {
            toast.error(Helper.errorHandler(err));
            return false;
        } finally {
            setSaving(false);
        }
    };

    const openAddReminder = () => {
        setEditingReminder(null);
        setReminderForm(emptyReminderForm);
        setAddReminderOpen(true);
    };

    const openEditReminder = (task: Task) => {
        setEditingReminder(task);
        setReminderForm({
            title: task.title || '',
            description: task.description || '',
            dueDate: task.dueDate || todayIso(),
            reminderTime: formatReminderTime(task.reminderTime) || '09:00',
            priority: task.priority || 'normal',
            repeat: task.repeat || 'none',
            repeatDays: task.repeatDays || '',
            repeatUntilOff: Boolean(task.repeatUntilOff),
            notifyInApp: task.notifyInApp ?? 1,
            notifyEmail: task.notifyEmail ?? 0,
            notifyWebPush: task.notifyWebPush ?? 0,
        });
        setAddReminderOpen(true);
    };

    const saveReminder = async () => {
        if (!reminderForm.title.trim()) return toast.error('Reminder title is required.');
        if (reminderForm.repeat === 'weekly' && !parseRepeatDays(reminderForm.repeatDays).length) return toast.error('Pick at least one weekday for a weekly reminder.');
        const url = editingReminder && typeof editingReminder.id === 'number'
            ? `/agent-handbook/tasks/${editingReminder.id}`
            : '/agent-handbook/tasks';
        const ok = await submit(url, reminderForm, editingReminder ? 'Reminder updated' : 'Reminder added');
        if (ok) {
            setReminderForm(emptyReminderForm);
            setEditingReminder(null);
            setAddReminderOpen(false);
        }
    };

    const turnOffRepeat = async (task: Task) => {
        if (typeof task.id !== 'number') return;
        await submit(`/agent-handbook/tasks/${task.id}/repeat-off`, {}, 'Repeat turned off');
    };

    const setReminderNotifications = async (task: Task, notifyInApp: boolean) => {
        if (typeof task.id !== 'number') return;
        await submit(
            `/agent-handbook/tasks/${task.id}/notify`,
            { notifyInApp: notifyInApp ? 1 : 0 },
            notifyInApp ? 'Notifications on' : 'Notifications off'
        );
    };

    const deleteReminder = async (task: Task) => {
        if (typeof task.id !== 'number') return;
        try {
            const res = await api.delete(`/agent-handbook/tasks/${task.id}`);
            if (res.data?.status) {
                toast.success('Reminder deleted');
                await loadDashboard();
                return;
            }
        } catch {
            /* try POST fallback */
        }
        await submit(`/agent-handbook/tasks/${task.id}/delete`, {}, 'Reminder deleted');
    };

    const confirmDeleteReminder = async () => {
        if (!deleteReminderTarget) return;
        await deleteReminder(deleteReminderTarget);
        setDeleteReminderTarget(null);
    };

    const claimLead = async (lead: Lead) => {
        setClaimingLeadId(lead.id);
        try {
            await submit(`/agent-handbook/leads/${lead.id}/claim`, {}, 'Lead claimed. Follow up within 24 hours.');
        } finally {
            setClaimingLeadId(null);
        }
    };

    const setLeadTemperature = async (lead: Lead, temperature: string) => {
        await submit(`/agent-handbook/leads/${lead.id}/temperature`, { temperature: temperature || null }, 'Lead temperature updated');
    };

    const openManageLead = (lead: Lead) => {
        setManageLead(lead);
        setNewRemark('');
        setReminderDays('1');
    };

    const addRemark = async () => {
        if (!manageLead) return;
        const text = newRemark.trim();
        if (!text) return;
        try {
            setAddingRemark(true);
            const response = await api.post(`/agent-handbook/leads/${manageLead.id}/remark`, { remark: text }, { headers: { 'Content-Type': 'application/json' } });
            if (!response.data.status) throw new Error(response.data.error);
            const created: LeadRemark = response.data.remark;
            // Append to the open lead so the new comment shows immediately with its timestamp.
            setManageLead(current => current ? { ...current, remarks: [...(current.remarks || []), created] } : current);
            setNewRemark('');
        } catch (err: any) {
            toast.error(Helper.errorHandler(err));
        } finally {
            setAddingRemark(false);
        }
    };

    const setLeadReminder = async () => {
        if (!manageLead) return;
        const days = Math.max(0, Math.min(60, Math.floor(Number(reminderDays) || 0)));
        try {
            setSettingReminder(true);
            const response = await api.post(`/agent-handbook/leads/${manageLead.id}/reminder`, { days }, { headers: { 'Content-Type': 'application/json' } });
            if (!response.data.status) throw new Error(response.data.error);
            toast.success(days === 0 ? 'Reminder set for today' : `Reminder set for ${days} day${days === 1 ? '' : 's'} from now`);
        } catch (err: any) {
            toast.error(Helper.errorHandler(err));
        } finally {
            setSettingReminder(false);
        }
    };

    const surrenderLead = async () => {
        if (!manageLead) return;
        const ok = await submit(`/agent-handbook/leads/${manageLead.id}/release`, {}, 'Lead surrendered for admin review');
        if (ok) setManageLead(null);
    };

    // Quick per-lead reminder (from the leads table). Reloads so the "reminder set" flag shows.
    const quickSetLeadReminder = async (lead: Lead, days: number) => {
        await submit(`/agent-handbook/leads/${lead.id}/reminder`, { days: Math.max(0, Math.min(60, Math.floor(days || 0))) }, days === 0 ? 'Reminder set for today' : `Reminder set for ${days} day${days === 1 ? '' : 's'} from now`);
    };

    const toggleReminderLead = (id: number) => setSelectedReminderLeadIds(current => current.includes(id) ? current.filter(x => x !== id) : [...current, id]);
    const exitReminderSelect = () => { setReminderSelectMode(false); setSelectedReminderLeadIds([]); };

    const setBulkReminder = async () => {
        if (!selectedReminderLeadIds.length) return;
        const days = Math.max(0, Math.min(60, Math.floor(Number(bulkReminderDays) || 0)));
        try {
            setBulkReminderBusy(true);
            const response = await api.post('/agent-handbook/lead-reminders', { leadIds: selectedReminderLeadIds, days }, { headers: { 'Content-Type': 'application/json' } });
            if (!response.data.status) throw new Error(response.data.error);
            toast.success(`Reminder set for ${response.data.count} lead${response.data.count === 1 ? '' : 's'}`);
            exitReminderSelect();
            await loadDashboard();
        } catch (err: any) {
            toast.error(Helper.errorHandler(err));
        } finally {
            setBulkReminderBusy(false);
        }
    };

    // Inline status: agents only toggle between Active (claimed) and Closed.
    const setLeadStatusInline = async (lead: Lead, value: string) => {
        if (value === 'surrender') {
            await submit(`/agent-handbook/leads/${lead.id}/release`, {}, 'Lead surrendered for admin review');
            return;
        }
        const status = value === 'closed' ? 'closed' : 'claimed';
        if (status === lead.status) return;
        await submit(`/agent-handbook/leads/${lead.id}/status`, { status }, value === 'closed' ? 'Lead closed' : 'Lead reactivated');
    };

    // Auto-saved inline remarks (debounced from the textarea).
    const saveLeadNotesInline = async (lead: Lead, notes: string) => {
        const response = await api.post(`/agent-handbook/leads/${lead.id}/notes`, { notes }, { headers: { 'Content-Type': 'application/json' } });
        if (!response.data.status) throw new Error(response.data.error);
    };

    const setLeadReminderLocal = (leadId: number, reminderAt: string | null) => {
        setData(prev => prev ? {
            ...prev,
            leadSessions: (prev.leadSessions || []).map(session => ({
                ...session,
                leads: (session.leads || []).map(lead => lead.id === leadId ? { ...lead, reminderAt } : lead),
            })),
        } : prev);
    };

    const removeLeadReminder = async (lead: Lead) => {
        try {
            const response = await api.delete(`/agent-handbook/leads/${lead.id}/reminder`);
            if (!response.data.status) throw new Error(response.data.error);
            setLeadReminderLocal(lead.id, null);
            toast.success('Reminder removed');
        } catch (err: any) {
            toast.error(Helper.errorHandler(err));
        }
    };

    const setTab = (value: string) => setSearchParams({ tab: value });

    const openReminders = () => {
        setNotificationOpen(false);
        setTab('reminders');
    };

    const quickTasks = data?.taskCenter.all.slice(0, 6) || [];
    const reminderCount = data?.taskCenter.all.length || 0;
    const announcementCount = data?.announcements?.length || 0;
    const leadView: 'market' | 'mine' = activeTab === 'my-leads' ? 'mine' : 'market';
    const heroLeadSessions = (data?.leadSessions || []).filter(session => Number(session.isPool || 0) !== 1);
    const leadMarketCount = heroLeadSessions.reduce((total, session) => total + (session.leads || []).filter(lead => lead.status === 'available').length, 0);
    const myLeadCount = heroLeadSessions.reduce((total, session) => total + (session.leads || []).filter(lead => Number(lead.claimedBy) === Number(agent?.id)).length, 0);
    const trainingItems = data?.trainingItems || [];
    const todayKeyForCalendar = isoDate(nowTick);
    const stageAnchors = computeStageAnchors(trainingItems, nowTick);
    // Place each training on the calendar. Scheduled → its date. Stage → the day it matters most:
    // a completed stage sits on the day it was COMPLETED (so the agent can see when they finished it),
    // an overdue active stage is pushed to today as a clear "due now" item, and locked stages sit on
    // their projected day.
    const trainingByDate = trainingItems.reduce<Record<string, TrainingItem[]>>((acc, item) => {
        let key = '';
        if (item.trainingMode === 'stage') {
            const a = stageAnchors.get(item.id);
            key = a ? (a.status === 'completed' ? (a.completedKey || a.dateKey) : a.dateKey) : '';
        } else {
            key = dateKeyFromValue(item.scheduledDate || item.startsAt);
        }
        if (key) {
            if (!acc[key]) acc[key] = [];
            acc[key].push(item);
        }
        return acc;
    }, {});
    // Completed stages get a soft completion span. Active overdue stages are shown on today instead
    // of drawing a red line through multiple days/months.
    const stageSegments = trainingItems.reduce<Record<string, StageSegment[]>>((acc, item) => {
        if (item.trainingMode !== 'stage') return acc;
        const anchor = stageAnchors.get(item.id);
        if (!anchor) return acc;
        // An overdue active stage shows the "Due now" chip on today, and a red strike running from
        // its original training date all the way to the day BEFORE today (spanning across months).
        if (anchor.status === 'active' && (anchor.overdueDays || 0) > 0) {
            const startKey = anchor.sourceDateKey || anchor.dateKey;
            const endKey = addDaysToKey(todayKeyForCalendar, -1);
            const cursor = new Date(`${startKey}T00:00:00`);
            const end = new Date(`${endKey}T00:00:00`);
            if (isValid(cursor) && isValid(end) && end >= cursor) {
                let guard = 0;
                while (cursor <= end && guard < 800) {
                    const key = isoDate(cursor);
                    (acc[key] = acc[key] || []).push({ item, isStart: key === startKey, isEnd: key === endKey, completed: false, overdueDays: anchor.overdueDays });
                    cursor.setDate(cursor.getDate() + 1);
                    guard += 1;
                }
            }
            return acc;
        }
        if (anchor.status !== 'completed') return acc;
        const startKey = anchor.sourceDateKey || anchor.dateKey;
        const endKey = anchor.completedKey || todayKeyForCalendar;
        const cursor = new Date(`${startKey}T00:00:00`);
        const end = new Date(`${endKey}T00:00:00`);
        if (!isValid(cursor) || !isValid(end) || end < cursor) return acc;
        const completed = anchor.status === 'completed';
        let guard = 0;
        while (cursor <= end && guard < 800) {
            const key = isoDate(cursor);
            (acc[key] = acc[key] || []).push({ item, isStart: key === startKey, isEnd: key === endKey, completed, overdueDays: anchor.overdueDays });
            cursor.setDate(cursor.getDate() + 1);
            guard += 1;
        }
        return acc;
    }, {});
    // Trainings relevant to a date = items anchored there plus any stage whose span covers it.
    const dayTrainings = (date: string): TrainingItem[] => {
        const base = trainingByDate[date] || [];
        const seen = new Set(base.map(item => item.id));
        const spanning = (stageSegments[date] || []).map(seg => seg.item).filter(item => !seen.has(item.id));
        return [...base, ...spanning];
    };
    const selectedDateTrainings = dayTrainings(selectedTrainingDate).sort((a, b) => {
        const aIndex = trainingItems.findIndex(item => item.id === a.id);
        const bIndex = trainingItems.findIndex(item => item.id === b.id);
        if ((a.startTime || '') !== (b.startTime || '')) return (a.startTime || '').localeCompare(b.startTime || '');
        return aIndex - bIndex;
    });
    const leadSessionProject = (session: LeadSession) => session.project || (session.leads || []).find(lead => lead.project)?.project || '';
    const projectLeadSessions = heroLeadSessions.filter(session => Boolean(leadSessionProject(session)));
    const leadProjects = Array.from(new Set(projectLeadSessions.map(session => leadSessionProject(session)).filter(Boolean))) as string[];
    const filteredLeadSessions = projectLeadSessions.filter(session => {
        const sessionProject = leadSessionProject(session);
        return leadProjectFilter === 'all' || sessionProject === leadProjectFilter;
    }).map(session => {
        const sessionProject = leadSessionProject(session);
        const leads = (session.leads || []).filter(lead => {
            const isMine = Number(lead.claimedBy) === Number(agent?.id);
            if (leadView === 'market' && lead.status !== 'available') return false;
            if (leadView === 'mine' && !isMine) return false;
            const project = lead.project || sessionProject;
            const searchable = [lead.name, project, lead.email, lead.contact, lead.status].filter(Boolean).join(' ').toLowerCase();
            return !leadSearch.trim() || searchable.includes(leadSearch.trim().toLowerCase());
        });
        return { ...session, project: sessionProject, leads };
    }).filter(session => (session.leads || []).length > 0 || leadSearch || leadView === 'mine');
    const visibleReminderLeadIds = filteredLeadSessions
        .flatMap(session => session.leads || [])
        .filter(lead => Number(lead.claimedBy) === Number(agent?.id) && !lead.claimedByOther && lead.status !== 'surrendered')
        .map(lead => lead.id);
    const allVisibleReminderLeadsSelected = visibleReminderLeadIds.length > 0 && visibleReminderLeadIds.every(id => selectedReminderLeadIds.includes(id));
    const toggleAllVisibleReminderLeads = () => {
        if (!visibleReminderLeadIds.length) return;
        setSelectedReminderLeadIds(current => {
            if (visibleReminderLeadIds.every(id => current.includes(id))) {
                return current.filter(id => !visibleReminderLeadIds.includes(id));
            }
            return Array.from(new Set([...current, ...visibleReminderLeadIds]));
        });
    };

    const navigateTrainingMonth = (dir: 1 | -1) => {
        setTrainingCursor(current => {
            const next = new Date(current.getFullYear(), current.getMonth() + dir, 1);
            setSelectedTrainingDate(isoDate(next));
            return next;
        });
    };

    if (loading) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                    <h5 className='h5'>Internal Agent Portal</h5>
                </div>
                <div className="flex items-center gap-2">
                    <Sheet
                        open={addReminderOpen}
                        onOpenChange={open => {
                            setAddReminderOpen(open);
                            if (!open) {
                                setEditingReminder(null);
                                setReminderForm(emptyReminderForm);
                            }
                        }}
                    >
                        <SheetContent side="right" className="flex w-full flex-col p-0 sm:max-w-md">
                            <SheetHeader className="border-b p-6">
                                <SheetTitle>{editingReminder ? 'Edit Personal Reminder' : 'Add Personal Reminder'}</SheetTitle>
                                <SheetDescription>Set a one-time or repeating reminder. Repeating reminders stay active until you turn them off.</SheetDescription>
                            </SheetHeader>
                            <div className="flex-1 space-y-4 overflow-y-auto p-6">
                                {webPushSupported && !webPushGranted && (
                                    <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
                                        <div className="text-sm font-medium text-blue-800">Enable web push notifications</div>
                                        <p className="mt-1 text-xs text-blue-600">Get browser push notifications even when the app is in the background.</p>
                                        <Button size="sm" className="mt-2" onClick={requestWebPush}>Enable Web Push</Button>
                                    </div>
                                )}
                                <Field label="Title">
                                    <Input
                                        placeholder="e.g. Follow up with client"
                                        value={reminderForm.title}
                                        onChange={e => setReminderForm({ ...reminderForm, title: e.target.value })}
                                    />
                                </Field>
                                <Field label="Notes">
                                    <Textarea
                                        value={reminderForm.description}
                                        onChange={e => setReminderForm({ ...reminderForm, description: e.target.value })}
                                    />
                                </Field>
                                <Field label="Due date">
                                    <DatePicker
                                        value={parsePickerDate(reminderForm.dueDate)}
                                        callback={date => setReminderForm({ ...reminderForm, dueDate: toIsoDate(date) })}
                                        className="w-full"
                                    />
                                </Field>
                                <Field label="Reminder time">
                                    <Input
                                        type="time"
                                        value={reminderForm.reminderTime || '09:00'}
                                        onChange={e => setReminderForm({ ...reminderForm, reminderTime: e.target.value })}
                                        className="pl-3"
                                    />
                                </Field>
                                <Field label="Priority">
                                    <Select value={reminderForm.priority} onValueChange={v => setReminderForm({ ...reminderForm, priority: v })}>
                                        <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="low">Low</SelectItem>
                                            <SelectItem value="normal">Normal</SelectItem>
                                            <SelectItem value="high">High</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </Field>
                                <div className="space-y-3 rounded-md border p-3">
                                    <label className="flex items-center justify-between text-sm font-medium">
                                        <span>Repeat</span>
                                        <Switch
                                            checked={reminderForm.repeat !== 'none'}
                                            onCheckedChange={on => setReminderForm({
                                                ...reminderForm,
                                                repeat: on ? 'weekly' : 'none',
                                                repeatDays: on ? (parseRepeatDays(reminderForm.repeatDays).length ? reminderForm.repeatDays : WEEKDAYS.map(d => d.key).join(',')) : '',
                                            })}
                                        />
                                    </label>
                                    {reminderForm.repeat !== 'none' && (
                                        <>
                                            <Field label="Repeat on">
                                                <div className="flex flex-wrap items-center gap-1.5">
                                                    {WEEKDAYS.map(day => {
                                                        const selected = parseRepeatDays(reminderForm.repeatDays).includes(day.key);
                                                        return (
                                                            <button
                                                                key={day.key}
                                                                type="button"
                                                                onClick={() => {
                                                                    const days = parseRepeatDays(reminderForm.repeatDays);
                                                                    const next = selected ? days.filter(d => d !== day.key) : [...days, day.key];
                                                                    setReminderForm({ ...reminderForm, repeatDays: next.join(',') });
                                                                }}
                                                                className={`h-8 w-11 rounded-md border text-xs font-medium transition-colors ${selected ? 'border-black bg-black text-white' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                                                                {day.label}
                                                            </button>
                                                        );
                                                    })}
                                                    {(() => {
                                                        const allKeys = WEEKDAYS.map(d => d.key);
                                                        const everyday = parseRepeatDays(reminderForm.repeatDays).length === allKeys.length;
                                                        return (
                                                            <button
                                                                type="button"
                                                                onClick={() => setReminderForm({ ...reminderForm, repeatDays: everyday ? '' : allKeys.join(',') })}
                                                                className="h-8 rounded-md border border-dashed border-gray-300 px-2 text-xs font-medium text-gray-600 hover:border-gray-400">
                                                                {everyday ? 'Clear' : 'Every day'}
                                                            </button>
                                                        );
                                                    })()}
                                                </div>
                                                <p className="mt-1.5 text-xs text-gray-500">Pick the days you want to be reminded — select all for every day, or deselect any day you don't want.</p>
                                            </Field>
                                            <label className="flex items-center gap-2 text-sm">
                                                <Switch
                                                    checked={Boolean(reminderForm.repeatUntilOff)}
                                                    onCheckedChange={checked => setReminderForm({ ...reminderForm, repeatUntilOff: checked })}
                                                />
                                                <span>Keep repeating until I manually turn it off</span>
                                            </label>
                                        </>
                                    )}
                                </div>
                                <ReminderChannels
                                    value={reminderForm}
                                    onChange={setReminderForm}
                                    webPushSupported={webPushSupported}
                                    webPushGranted={webPushGranted}
                                />
                            </div>
                            <div className="border-t p-4">
                                <Button className="w-full" onClick={saveReminder} disabled={saving}>
                                    <Send className="mr-2 h-4 w-4" />
                                    {editingReminder ? 'Update Reminder' : 'Save Reminder'}
                                </Button>
                            </div>
                        </SheetContent>
                    </Sheet>

                    <Sheet open={notificationOpen} onOpenChange={setNotificationOpen}>
                        <SheetTrigger asChild>
                            <Button type="button" variant="outline" size="icon" className="relative">
                                <Bell className="h-4 w-4" />
                                {reminderCount > 0 && (
                                    <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold text-white">
                                        {reminderCount > 99 ? '99+' : reminderCount}
                                    </span>
                                )}
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="right" className="flex w-full flex-col p-0 sm:max-w-md">
                            <SheetHeader className="border-b p-6">
                                <SheetTitle className="flex items-center gap-2"><Bell className="h-5 w-5" />Reminders</SheetTitle>
                                <SheetDescription>Quick view of your active reminders and follow-ups.</SheetDescription>
                            </SheetHeader>
                            <div className="flex-1 space-y-3 overflow-y-auto p-6">
                                {quickTasks.length === 0 && (
                                    <div className="py-10 text-center text-sm text-gray-500">No reminders right now.</div>
                                )}
                                {quickTasks.map(task => (
                                    <button key={task.id} type="button" className="w-full cursor-pointer rounded-md border p-3 text-left hover:bg-gray-50" onClick={openReminders}>
                                        <div className="font-medium text-gray-950">{task.title}</div>
                                        {task.description && <div className="mt-1 line-clamp-2 text-sm text-gray-600">{task.description}</div>}
                                        <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-500">
                                            {task.dueDate && <span>{formatReminderDate(task.dueDate)} {formatReminderTime(task.reminderTime)}</span>}
                                            {task.priority && <Badge variant={task.priority === 'high' ? 'destructive' : 'secondary'}>{task.priority}</Badge>}
                                            {task.repeat && task.repeat !== 'none' && (
                                                <Badge variant="outline" className="flex items-center gap-1">
                                                    <RefreshCw className="h-3 w-3" />
                                                    {task.repeat === 'weekly' && parseRepeatDays(task.repeatDays).length
                                                        ? parseRepeatDays(task.repeatDays).map(d => WEEKDAYS.find(w => w.key === d)?.label).filter(Boolean).join(', ')
                                                        : task.repeat}
                                                </Badge>
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                            <div className="border-t p-4">
                                <Button type="button" className="w-full" onClick={openReminders}>View all</Button>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>

            {/* Agent progress header */}
            {agent && (() => {
                const items = data?.trainingItems || [];
                // Progress is based on STAGE training only.
                const stages = items.filter(item => item.trainingMode === 'stage');
                const total = stages.length;
                const completed = stages.filter(item => Boolean(item.progress?.completedAt)).length;
                const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
                const currentStage = stages.find(item => !item.progress?.completedAt) || null;
                const currentState = currentStage ? trainingDisplayState(currentStage, 0, stages, nowTick) : null;
                const currentRank = data?.currentRank;
                const accent = currentRank ? rankColor(currentRank) : '#0ea5e9';
                const name = `${agent.firstName || ''} ${agent.lastName || ''}`.trim() || agent.email || 'Agent';
                const initials = name.split(' ').map((part: string) => part.charAt(0)).slice(0, 2).join('').toUpperCase();
                return (
                    <div className="rounded-xl bg-white">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                            <Avatar className="h-20 w-20 ring-2 sm:h-24 sm:w-24" style={{ boxShadow: `0 0 0 3px ${accent}33` }}>
                                {agent.avatar && <AvatarImage src={`${s3BaseUrl}/${String(agent.avatar).replace(/^\//, '')}`} />}
                                <AvatarFallback className="text-xl font-semibold">{initials || 'A'}</AvatarFallback>
                            </Avatar>
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                    <p className="truncate text-base font-semibold text-gray-900">{name}</p>
                                    {currentRank ? <RankChip rank={currentRank} /> : <span className="text-xs text-gray-400">No rank assigned</span>}
                                </div>
                                <div className="mt-2">
                                    <div className="flex items-center justify-between text-xs text-gray-500">
                                        <span>Stage progress</span>
                                        <span className="font-medium text-gray-700">{completed}/{total} · {pct}%</span>
                                    </div>
                                    <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-gray-100">
                                        <div
                                            className="h-full rounded-full bg-black transition-[width] duration-500"
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                </div>
                                {currentStage ? (
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/trainings/${currentStage.id}`)}
                                        className="mt-3 flex w-full items-center justify-between gap-3 rounded-lg border bg-gray-50/60 px-3 py-2 text-left transition-colors hover:bg-gray-50"
                                    >
                                        <div className="min-w-0">
                                            <div className="text-[11px] uppercase tracking-wide text-gray-400">Current stage</div>
                                            <div className="truncate text-sm font-medium text-gray-900">{currentStage.title}</div>
                                        </div>
                                        {currentState?.locked
                                            ? <Badge variant="outline" className="shrink-0 gap-1 text-muted-foreground"><Lock className="h-3 w-3" />Locked</Badge>
                                            : (currentState?.procrastinationDays || 0) > 0
                                                ? <Badge variant="outline" className="shrink-0 border-red-200 text-red-600">{currentState?.procrastinationDays}d due</Badge>
                                                : <Badge variant="outline" className="shrink-0 border-violet-200 text-violet-700">Ready</Badge>}
                                    </button>
                                ) : total > 0 ? (
                                    <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700">All stages completed 🎉</div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Tabs */}
            <div className="no-scrollbar flex gap-5 overflow-x-auto border-b border-gray-200">
                {tabs.map(tab => {
                    const Icon = tab.icon;
                    const active = activeTab === tab.value;
                    const count = tab.value === 'announcements' ? announcementCount : tab.value === 'lead-market' ? leadMarketCount : tab.value === 'my-leads' ? myLeadCount : undefined;
                    return (
                        <button
                            key={tab.value}
                            type="button"
                            onClick={() => setTab(tab.value)}
                            className={`relative flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap pb-3 text-sm transition-colors ${active ? 'font-semibold text-black' : 'text-gray-500 hover:text-gray-900'}`}
                        >
                            <Icon className="h-4 w-4" />
                            {tab.label}
                            {Boolean(count) && (
                                <span className={cx('rounded-full px-1.5 py-0.5 text-[10px] font-semibold', active ? 'bg-black text-white' : 'bg-gray-100 text-gray-500')}>
                                    {count > 99 ? '99+' : count}
                                </span>
                            )}
                            {active && (
                                <motion.div
                                    layoutId="agent-handbook-hero-tab-underline"
                                    className="absolute bottom-0 left-0 right-0 h-[2px] bg-black"
                                    transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                                />
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Tab Content */}
            <div className="space-y-5">

                {/* ── Announcements ── */}
                {activeTab === 'announcements' && (
                    <div className="space-y-4">
                        <SectionHeading title="Announcements" description="Latest internal updates and scheduled notices." />
                        {(data?.announcements || []).length === 0 && (
                            <div className="rounded-md border border-dashed p-10 text-center text-sm text-gray-500">No announcements yet.</div>
                        )}
                        {(data?.announcements || []).length > 0 && (
                            <div className="grid gap-6 lg:grid-cols-2">
                                {(data?.announcements || []).map(announcement => {
                                    const image = mediaUrl(announcement.imageObjectKey || announcement.imageUrl) || '/xpillar-icon.png';
                                    return (
                                        <article key={announcement.id} className="overflow-hidden bg-white">
                                            <div className="aspect-[16/9] overflow-hidden bg-gray-100">
                                                <img src={image} alt={announcement.title} className="h-full w-full object-cover" />
                                            </div>
                                            <div className="py-5">
                                                <h2 className="text-lg font-semibold text-gray-950">{announcement.title}</h2>
                                                <p className="mt-2 text-sm leading-6 text-gray-700">{announcement.description || announcement.message || ''}</p>
                                                {announcement.message && announcement.description && (
                                                    <p className="mt-3 text-sm leading-6 text-gray-600">{announcement.message}</p>
                                                )}
                                                <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                                    {announcement.scheduledAt && <span>{formatDateTimeLabel(announcement.scheduledAt)}</span>}
                                                    {Boolean(announcement.notifyInApp) && <Badge variant="outline">In-app</Badge>}
                                                    {Boolean(announcement.notifyWebPush) && <Badge variant="outline">Web push</Badge>}
                                                    {Boolean(announcement.notifyEmail) && <Badge variant="outline">Email</Badge>}
                                                </div>
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* ── Manual ── */}
                {activeTab === 'manual' && (
                    <div className="space-y-4">
                        <SectionHeading title="Xpillar Manual" description="Reference notes and operating standards from admin." />
                        {(data?.manualSections || []).length === 0 && (
                            <div className="rounded-md border border-dashed p-10 text-center text-sm text-gray-500">No manuals published yet.</div>
                        )}
                        {(data?.manualSections || []).length > 0 && (
                            <div className="overflow-hidden">
                                <Table className="border-0">
                                    <TableHeader>
                                        <TableRow >
                                            <TableHead className="w-[10rem]">Category</TableHead>
                                            <TableHead>Title</TableHead>
                                            <TableHead className="min-w-[12rem] max-w-[28rem]">Summary</TableHead>
                                            <TableHead className="w-[14rem]">Files</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {(data?.manualSections || []).map(section => (
                                            <TableRow key={section.id}>
                                                <TableCell><Badge variant="secondary">{section.category}</Badge></TableCell>
                                                <TableCell className="font-medium text-gray-950">{section.title}</TableCell>
                                                <TableCell className="max-w-md whitespace-normal text-sm text-gray-600">
                                                    {section.content || '—'}
                                                </TableCell>
                                                <TableCell className="whitespace-normal">
                                                    {Boolean(section.files?.length) ? (
                                                        <div className="space-y-2">
                                                            {section.files?.map(file => {
                                                                const isImage = file.fileKind === 'image';
                                                                const href = mediaUrl(file.objectKey || file.url);
                                                                return (
                                                                    <Link
                                                                        key={file.id}
                                                                        to={`/manuals/${file.id}`}
                                                                        className="flex max-w-full items-center gap-2 rounded-md border bg-white p-2 text-xs font-medium text-foreground transition-colors hover:bg-muted/40"
                                                                    >
                                                                        {isImage ? (
                                                                            <img src={href} alt="" className="h-10 w-14 shrink-0 rounded object-cover" />
                                                                        ) : (
                                                                            <span className="flex h-10 w-14 shrink-0 items-center justify-center rounded bg-muted text-[10px] uppercase text-muted-foreground">PDF</span>
                                                                        )}
                                                                        <span className="min-w-0 truncate">{file.name}</span>
                                                                    </Link>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-gray-400">—</span>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </div>
                )}

                {/* ── Leads ── */}
                {(activeTab === 'lead-market' || activeTab === 'my-leads') && (
                    <div className="space-y-4">
                        <SectionHeading
                            title={activeTab === 'my-leads' ? 'My Leads' : 'Lead Market'}
                            description={activeTab === 'my-leads' ? 'Review your claimed leads, update status, and surrender leads when needed.' : 'Claim one open lead per session and follow up within 24 hours to keep it assigned to you.'}
                        />
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="text-sm text-gray-500">{activeTab === 'my-leads' ? `${myLeadCount} assigned` : `${leadMarketCount} available`}</div>
                            <div className="flex flex-wrap items-center gap-2">
                                <Input className="h-9 w-56 text-sm" placeholder="Search email, contact, name" value={leadSearch} onChange={event => setLeadSearch(event.target.value)} />
                                <Select value={leadProjectFilter} onValueChange={setLeadProjectFilter}>
                                    <SelectTrigger className="h-9 w-44 text-sm"><SelectValue placeholder="Project" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all" className="text-sm">All projects</SelectItem>
                                        {leadProjects.map(project => <SelectItem key={project} value={project} className="text-sm">{project}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                {activeTab === 'my-leads' && (
                                    reminderSelectMode ? (
                                        <Button type="button" variant="ghost" size="sm" className="h-9" onClick={exitReminderSelect}>Cancel</Button>
                                    ) : (
                                        <Button type="button" variant="outline" size="sm" className="h-9" onClick={() => setReminderSelectMode(true)}>
                                            <Bell className="mr-2 h-4 w-4" />
                                            Add Reminder
                                        </Button>
                                    )
                                )}
                            </div>
                        </div>
                        {activeTab === 'my-leads' && reminderSelectMode && (
                            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-indigo-50/50 px-3 py-2">
                                <div className="text-sm font-medium text-gray-700">{selectedReminderLeadIds.length} lead{selectedReminderLeadIds.length === 1 ? '' : 's'} selected</div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-sm text-gray-500">Remind in</span>
                                    <Input type="number" min={0} max={60} value={bulkReminderDays} onChange={e => setBulkReminderDays(e.target.value)} className="h-9 w-20" />
                                    <span className="text-sm text-gray-500">days</span>
                                    <Button type="button" size="sm" className="h-9" disabled={bulkReminderBusy || selectedReminderLeadIds.length === 0} onClick={setBulkReminder}>
                                        {bulkReminderBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Bell className="mr-2 h-4 w-4" />}
                                        Set reminder
                                    </Button>
                                </div>
                            </div>
                        )}
                        {(data?.leadSessions || []).length === 0 && (
                            <div className="rounded-md border border-dashed p-10 text-center text-sm text-gray-500">No lead sessions are open right now.</div>
                        )}
                        {(data?.leadSessions || []).length > 0 && filteredLeadSessions.length === 0 && (
                            <div className="rounded-md border border-dashed p-10 text-center text-sm text-gray-500">No leads match your filters.</div>
                        )}
                        <Accordion type="multiple" defaultValue={filteredLeadSessions.map(session => String(session.id))} className="space-y-3">
                            {filteredLeadSessions.map(session => {
                                const isLive = session.computedStatus === 'live';
                                return (
                                    <AccordionItem key={session.id} value={String(session.id)} className="rounded-md border bg-background px-4">
                                        <AccordionTrigger className="cursor-pointer py-4 hover:no-underline">
                                            <div className="flex w-full flex-col gap-2 pr-3 text-left sm:flex-row sm:items-start sm:justify-between">
                                                <div>
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <h2 className="text-base font-semibold text-gray-950">{session.title}</h2>
                                                        <LeadStatusBadge status={session.computedStatus || session.status} />
                                                        <Badge variant="outline">{session.leads?.length || 0} lead{(session.leads?.length || 0) === 1 ? '' : 's'}</Badge>
                                                    </div>
                                                    <div className="mt-1 text-sm text-gray-500">{session.project}</div>
                                                    {session.description && <p className="mt-2 max-w-3xl text-sm text-gray-600">{session.description}</p>}
                                                </div>
                                                <div className="text-left text-xs text-gray-500 sm:text-right">
                                                    <div>{session.liveAt ? `Live ${formatDateTimeLabel(session.liveAt)}` : 'Manual live window'}</div>
                                                    <div>{session.closeAt ? `Closes ${formatDateTimeLabel(session.closeAt)}` : 'No close time'}</div>
                                                </div>
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent className="space-y-3 pb-4">
                                    <div className="overflow-x-auto">
                                        <Table className="min-w-[860px]">
                                            <TableHeader>
                                                <TableRow>
                                                    {activeTab === 'my-leads' && reminderSelectMode && (
                                                        <TableHead className="w-[44px]">
                                                            <Checkbox
                                                                checked={allVisibleReminderLeadsSelected}
                                                                disabled={visibleReminderLeadIds.length === 0}
                                                                onCheckedChange={toggleAllVisibleReminderLeads}
                                                                aria-label="Select all visible leads"
                                                            />
                                                        </TableHead>
                                                    )}
                                                    <TableHead>Name</TableHead>
                                                    <TableHead>Project</TableHead>
                                                    <TableHead>Email</TableHead>
                                                    <TableHead>Contact</TableHead>
                                                    <TableHead className="w-[130px]">Temperature</TableHead>
                                                    <TableHead>Status</TableHead>
                                                    {activeTab === 'my-leads'
                                                        ? <TableHead className="w-[300px]">Remarks</TableHead>
                                                        : <TableHead className="w-[160px] text-right">Action</TableHead>}
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {(session.leads || []).length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={activeTab === 'my-leads' && reminderSelectMode ? 8 : 7} className="py-8 text-center text-sm text-gray-500">No leads in this session yet.</TableCell>
                                                    </TableRow>
                                                )}
                                                {(session.leads || []).map(lead => {
                                                    const isMine = Number(lead.claimedBy) === Number(agent?.id);
                                                    const canClaim = isLive && lead.status === 'available';
                                                    // Manage stays available for any lead the agent owns — even after it's closed.
                                                    const canManage = isMine && !lead.claimedByOther;
                                                    const canRemind = canManage && lead.status !== 'surrendered';
                                                    return (
                                                        <TableRow key={lead.id} className={activeTab === 'my-leads' && reminderSelectMode && selectedReminderLeadIds.includes(lead.id) ? 'bg-indigo-50/50' : undefined}>
                                                            {activeTab === 'my-leads' && reminderSelectMode && (
                                                                <TableCell>
                                                                    {canRemind
                                                                        ? <Checkbox checked={selectedReminderLeadIds.includes(lead.id)} onCheckedChange={() => toggleReminderLead(lead.id)} />
                                                                        : <span className="text-xs text-gray-300">—</span>}
                                                                </TableCell>
                                                            )}
                                                            <TableCell className="font-medium text-gray-950">
                                                                <div className="flex items-center gap-1.5">
                                                                    <span>{lead.name}</span>
                                                                    {lead.reminderAt && (
                                                                        <span
                                                                            className="inline-flex items-center gap-1 rounded-full bg-indigo-50 py-0.5 pl-1.5 pr-1 text-[10px] font-medium text-indigo-600"
                                                                            title={`Reminder set for ${new Date(`${String(lead.reminderAt).slice(0, 10)}T00:00:00`).toLocaleDateString()}`}
                                                                        >
                                                                            <BellRing className="h-3 w-3" />
                                                                            {new Date(`${String(lead.reminderAt).slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                                                            <button
                                                                                type="button"
                                                                                title="Remove reminder"
                                                                                onClick={() => removeLeadReminder(lead)}
                                                                                className="ml-0.5 rounded-full p-0.5 hover:bg-indigo-100"
                                                                            >
                                                                                <X className="h-2.5 w-2.5" />
                                                                            </button>
                                                                        </span>
                                                                    )}
                                                                    {canManage && lead.status === 'claimed' && lead.claimExpiresAt && (
                                                                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                                                                            Due {formatDateTimeLabel(lead.claimExpiresAt)}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell>{lead.project || session.project || '-'}</TableCell>
                                                            <TableCell>{lead.claimedByOther ? '-' : lead.email || '-'}</TableCell>
                                                            <TableCell>{lead.claimedByOther ? '-' : lead.contact || '-'}</TableCell>
                                                            <TableCell>
                                                                {isMine
                                                                    ? <LeadTemperaturePicker value={lead.temperature} disabled={saving} onChange={value => setLeadTemperature(lead, value)} />
                                                                    : <LeadTemperatureBadge value={lead.claimedByOther ? null : lead.temperature} />}
                                                            </TableCell>
                                                            <TableCell>
                                                                <div className="space-y-1">
                                                                    {canManage && lead.status !== 'surrendered' ? (
                                                                        <Select value={lead.status === 'closed' ? 'closed' : 'active'} disabled={saving} onValueChange={value => setLeadStatusInline(lead, value)}>
                                                                            <SelectTrigger className="h-8 w-[120px] text-xs"><SelectValue /></SelectTrigger>
                                                                            <SelectContent>
                                                                                <SelectItem value="active" className="text-xs">Active</SelectItem>
                                                                                <SelectItem value="closed" className="text-xs">Closed</SelectItem>
                                                                                <SelectItem value="surrender" className="text-xs text-red-600">Surrender</SelectItem>
                                                                            </SelectContent>
                                                                        </Select>
                                                                    ) : (
                                                                        <LeadStatusBadge status={lead.claimedByOther ? 'claimed' : lead.status} />
                                                                    )}
                                                                </div>
                                                            </TableCell>
                                                            {activeTab === 'my-leads' ? (
                                                                <TableCell className="align-top">
                                                                    {canManage
                                                                        ? <InlineLeadNotes lead={lead} onSave={saveLeadNotesInline} />
                                                                        : <span className="text-xs text-gray-400">—</span>}
                                                                </TableCell>
                                                            ) : (
                                                                <TableCell className="text-right">
                                                                    {canClaim
                                                                        ? (
                                                                            <Button type="button" size="sm" disabled={claimingLeadId === lead.id} onClick={() => claimLead(lead)}>
                                                                                {claimingLeadId === lead.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Users className="mr-2 h-4 w-4" />}
                                                                                Claim
                                                                            </Button>
                                                                        )
                                                                        : <span className="text-xs text-gray-400">—</span>}
                                                                </TableCell>
                                                            )}
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                );
                            })}
                        </Accordion>
                    </div>
                )}

                {/* ── Reminders ── */}
                {activeTab === 'reminders' && (
                    <div className="space-y-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <SectionHeading
                                title="Reminders"
                                description="Your personal reminders. Toggle in-app notifications, turn off repeating cycles, or delete a reminder."
                            />
                            <Button type="button" variant="outline" size="sm" onClick={openAddReminder}>
                                <Bell className="mr-2 h-4 w-4" />
                                Add Reminder
                            </Button>
                        </div>
                        {(data?.taskCenter.custom || []).length === 0 && (
                            <div className="rounded-md border border-dashed p-10 text-center text-sm text-gray-500">No reminders yet. Use Add Reminder to create one.</div>
                        )}
                        {(data?.taskCenter.custom || []).length > 0 && (
                            <TooltipProvider>
                                <Table className="mt-3">
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-[400px]">Reminder</TableHead>
                                            <TableHead>Schedule</TableHead>
                                            <TableHead>Priority</TableHead>
                                            <TableHead>Repeat</TableHead>
                                            <TableHead>Notify</TableHead>
                                            <TableHead>Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {(data?.taskCenter.custom || []).map(task => (
                                            <TableRow key={task.id}>
                                                {/* Reminder title + description */}
                                                <TableCell className="w-[400px] max-w-[400px]">
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100">
                                                            <Bell className="h-5 w-5 text-gray-500" />
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <div className="break-words font-medium text-gray-950">{task.title}</div>
                                                            {task.description && (
                                                                <div className="mt-0.5 line-clamp-2 text-xs text-gray-500">{task.description}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </TableCell>

                                                {/* Schedule */}
                                                <TableCell>
                                                    <div className="text-sm text-gray-700">{formatReminderDate(task.dueDate)}</div>
                                                    {task.reminderTime && (
                                                        <div className="text-xs text-gray-500">{formatReminderTime(task.reminderTime)}</div>
                                                    )}
                                                </TableCell>

                                                {/* Priority */}
                                                <TableCell>
                                                    {task.priority ? (
                                                        <Badge variant={task.priority === 'high' ? 'destructive' : 'secondary'}>
                                                            {task.priority}
                                                        </Badge>
                                                    ) : (
                                                        <span className="text-xs text-gray-400">—</span>
                                                    )}
                                                </TableCell>

                                                {/* Repeat */}
                                                <TableCell>
                                                    {task.repeat && task.repeat !== 'none' ? (
                                                        <div className="space-y-1.5">
                                                            <Badge variant="outline" className="inline-flex items-center gap-1">
                                                                <RefreshCw className="h-3 w-3" />
                                                                {task.repeat === 'weekly' && parseRepeatDays(task.repeatDays).length
                                                                    ? parseRepeatDays(task.repeatDays).map(d => WEEKDAYS.find(w => w.key === d)?.label).filter(Boolean).join(', ')
                                                                    : task.repeat}
                                                            </Badge>
                                                            {typeof task.id === 'number' && task.repeatUntilOff && (
                                                                <div>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="ghost"
                                                                        className="h-7 px-2 text-xs text-gray-500 hover:text-gray-800"
                                                                        onClick={() => turnOffRepeat(task)}
                                                                    >
                                                                        Stop repeat
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-gray-400">—</span>
                                                    )}
                                                </TableCell>

                                                {/* Notify toggle */}
                                                <TableCell>
                                                    {typeof task.id === 'number' ? (
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <div>
                                                                    <Switch
                                                                        checked={Boolean(task.notifyInApp)}
                                                                        onCheckedChange={v => setReminderNotifications(task, Boolean(v))}
                                                                        disabled={saving}
                                                                    />
                                                                </div>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <p>{Boolean(task.notifyInApp) ? 'Notifications on' : 'Notifications off'}</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    ) : (
                                                        <span className="text-xs text-gray-400">—</span>
                                                    )}
                                                </TableCell>

                                                {/* Actions */}
                                                <TableCell>
                                                    <div className="flex items-center gap-1">
                                                        {typeof task.id === 'number' && (
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        type="button"
                                                                        size="sm"
                                                                        variant="outline"

                                                                        onClick={() => openEditReminder(task)}
                                                                    >
                                                                        <Edit className="h-4 w-4" color="black" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent><p>Edit Reminder</p></TooltipContent>
                                                            </Tooltip>
                                                        )}
                                                        {typeof task.id === 'number' && (
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        type="button"
                                                                        size="sm"
                                                                        variant="destructive"

                                                                        onClick={() => setDeleteReminderTarget(task)}
                                                                    >
                                                                        <Trash2 className="h-4 w-4" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent><p>Delete Reminder</p></TooltipContent>
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TooltipProvider>
                        )}
                    </div>
                )}

                {/* ── Training ── */}
                {activeTab === 'training' && (
                    <div className="space-y-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <SectionHeading title="Training Calendar" description="Pick a date to view its lessons. Overdue stages are highlighted on today so you know what needs action now." />
                            <div className="flex items-center gap-1.5">
                                <Button type="button" variant="outline" size="icon" className="w-8" onClick={() => navigateTrainingMonth(-1)}>
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <div className="min-w-[150px] text-center text-sm font-semibold text-gray-900">
                                    {MONTH_NAMES[trainingCursor.getMonth()]} {trainingCursor.getFullYear()}
                                </div>
                                <Button type="button" variant="outline" size="icon" className="w-8" onClick={() => navigateTrainingMonth(1)}>
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                        {trainingItems.length === 0 && (
                            <div className="rounded-md border border-dashed p-10 text-center text-sm text-gray-500">No training modules published yet.</div>
                        )}
                        {trainingItems.length > 0 && (
                            <div className="space-y-5">
                                <TrainingCalendar
                                    cursor={trainingCursor}
                                    selectedDate={selectedTrainingDate}
                                    trainingsByDate={trainingByDate}
                                    segmentsByDate={stageSegments}
                                    stageAnchors={stageAnchors}
                                    onSelectDate={(date) => {
                                        const items = dayTrainings(date);
                                        if (!items.length) {
                                            toast(`Nothing on ${format(parsePickerDate(date) || new Date(date), 'MMM d')}`);
                                            return;
                                        }
                                        setSelectedTrainingDate(date);
                                        setDayDialogOpen(true);
                                    }}
                                />

                                <Dialog open={dayDialogOpen} onOpenChange={setDayDialogOpen}>
                                    <DialogContent className="max-w-lg overflow-hidden p-0">
                                        <DialogHeader className="sr-only">
                                            <DialogTitle>Trainings on this date</DialogTitle>
                                            <DialogDescription>Trainings scheduled or unlocked on the selected date.</DialogDescription>
                                        </DialogHeader>
                                        <div className="max-h-[70vh] overflow-y-auto">
                                            <SelectedTrainingDateList
                                                date={selectedTrainingDate}
                                                trainings={selectedDateTrainings}
                                                allTrainings={trainingItems}
                                                now={nowTick}
                                                onOpen={(item) => { setDayDialogOpen(false); navigate(`/trainings/${item.id}`); }}
                                            />
                                        </div>
                                    </DialogContent>
                                </Dialog>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Manage claimed lead */}
            <Sheet open={Boolean(manageLead)} onOpenChange={open => !open && setManageLead(null)}>
                <SheetContent side="right" className="flex w-full flex-col p-0 sm:max-w-md">
                    <SheetHeader className="border-b p-6">
                        <SheetTitle>{manageLead?.name || 'Lead'}</SheetTitle>
                        <SheetDescription>Add remarks for this lead.</SheetDescription>
                    </SheetHeader>
                    <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
                        <div className="grid grid-cols-3 gap-3 text-sm">
                            <div><div className="text-xs text-gray-500">Project</div><div className="font-medium">{manageLead?.project || '-'}</div></div>
                            <div><div className="text-xs text-gray-500">Email</div><div className="truncate font-medium">{manageLead?.email || '-'}</div></div>
                            <div><div className="text-xs text-gray-500">Contact</div><div className="font-medium">{manageLead?.contact || '-'}</div></div>
                        </div>
                        <div className="space-y-2">
                            <div className="text-sm font-medium text-gray-900">Remarks</div>
                            <div className="flex max-h-72 min-h-[80px] flex-col gap-2 overflow-y-auto rounded-lg border bg-gray-50 p-3">
                                {(manageLead?.remarks || []).length === 0 && (
                                    <p className="m-auto text-xs text-gray-400">No remarks yet. Start the conversation below.</p>
                                )}
                                {(manageLead?.remarks || []).map(remark => (
                                    <div key={remark.id} className="flex flex-col items-end">
                                        <div className="max-w-[85%] whitespace-pre-line rounded-2xl rounded-br-sm bg-indigo-600 px-3 py-2 text-sm text-white shadow-sm">
                                            {remark.remark}
                                        </div>
                                        <span className="mt-0.5 pr-1 text-[10px] text-gray-400">{formatDateTimeLabel(remark.createdAt)}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="space-y-2">
                                <Textarea
                                    rows={2}
                                    value={newRemark}
                                    onChange={e => setNewRemark(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void addRemark(); } }}
                                    placeholder="Type a remark and press Enter…"
                                    className="w-full"
                                />
                                <Button type="button" className="w-full" disabled={addingRemark || !newRemark.trim()} onClick={addRemark}>
                                    {addingRemark ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                                    Send
                                </Button>
                            </div>
                        </div>
                        {manageLead?.status === 'claimed' && (
                            <div className="space-y-3 rounded-lg border bg-gray-50/60 p-3">
                                <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                                    <Bell className="h-4 w-4" />
                                    Remind me to follow up
                                </div>
                                <div className="flex items-end gap-2">
                                    <Field label="In (days)">
                                        <Input type="number" min={0} max={60} value={reminderDays} onChange={e => setReminderDays(e.target.value)} className="w-24" />
                                    </Field>
                                    <Button type="button" variant="outline" size="sm" disabled={settingReminder} onClick={setLeadReminder}>
                                        {settingReminder ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Bell className="mr-2 h-4 w-4" />}
                                        Set reminder
                                    </Button>
                                </div>
                                <p className="text-[11px] text-gray-400">You'll get an in-app and push notification when it's due.</p>
                            </div>
                        )}
                    </div>
                    {manageLead?.status !== 'surrendered' && (
                        <div className="border-t p-4">
                            <Button type="button" variant="destructive" className="w-full" disabled={saving} onClick={surrenderLead}>
                                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Surrender lead
                            </Button>
                        </div>
                    )}
                </SheetContent>
            </Sheet>

            {/* Delete confirmation */}
            <AlertDialog open={Boolean(deleteReminderTarget)} onOpenChange={open => !open && setDeleteReminderTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete reminder?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will remove the reminder from your handbook task list.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            disabled={saving}
                            onClick={event => {
                                event.preventDefault();
                                void confirmDeleteReminder();
                            }}
                        >
                            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionHeading = ({ title, description }: { title: string; description: string }) => (
    <div>
        <h2 className="text-xl font-semibold text-gray-950">{title}</h2>
        <p className="mt-1 text-sm text-gray-600">{description}</p>
    </div>
);

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="space-y-1.5">
        <Label className="text-xs font-medium text-gray-600">{label}</Label>
        {children}
    </div>
);

const TrainingCalendar = ({
    cursor,
    selectedDate,
    trainingsByDate,
    segmentsByDate,
    stageAnchors,
    onSelectDate,
}: {
    cursor: Date;
    selectedDate: string;
    trainingsByDate: Record<string, TrainingItem[]>;
    segmentsByDate: Record<string, StageSegment[]>;
    stageAnchors: Map<number, StageAnchor>;
    onSelectDate: (date: string) => void;
}) => {
    const todayKey = isoDate(new Date());
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const firstDow = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells: Array<{ date: string; day: number } | null> = [];
    for (let index = 0; index < firstDow; index++) cells.push(null);
    for (let day = 1; day <= daysInMonth; day++) cells.push({ date: `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`, day });
    while (cells.length % 7 !== 0) cells.push(null);

    return (
        <div className="overflow-hidden rounded-lg border bg-white">
            <div className="grid grid-cols-7 bg-gray-50">
                {DAY_KEYS.map(day => (
                    <div key={day} className="py-3 text-center text-[10px] font-semibold uppercase tracking-wider text-gray-400">{day}</div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-px bg-gray-100">
                {cells.map((cell, index) => {
                    const rows = cell ? trainingsByDate[cell.date] || [] : [];
                    const segments = cell ? segmentsByDate[cell.date] || [] : [];
                    const struckPending = segments.some(seg => !seg.completed);
                    const struckDone = segments.length > 0 && !struckPending;
                    const struck = segments.length > 0;
                    const selected = Boolean(cell && cell.date === selectedDate);
                    const isPlainDay = !(cell && (cell.date === todayKey || selected));
                    return (
                        <button
                            key={cell?.date || index}
                            type="button"
                            disabled={!cell}
                            onClick={() => cell && onSelectDate(cell.date)}
                            className={cx(
                                'min-h-[112px] overflow-hidden p-2.5 text-left transition-colors',
                                !cell && 'bg-gray-50',
                                cell && !selected && 'bg-white hover:bg-indigo-50/40',
                                cell && 'cursor-pointer',
                                selected && 'rounded-xl bg-indigo-50 ring-1 ring-inset ring-indigo-300'
                            )}
                        >
                            {cell && (
                                <>
                                    {/* Soft line struck all the way across a stage's overdue/completed span */}
                                    <div className="relative flex items-center">
                                        {struck && <span className={cx('pointer-events-none absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2 rounded-full', struckDone ? 'bg-emerald-300' : 'bg-red-300')} />}
                                        <span className={cx(
                                            'relative z-10 flex h-7 w-7 items-center justify-center rounded-full text-sm font-medium transition-colors',
                                            cell.date === todayKey ? 'bg-gray-900 text-white shadow-sm'
                                                : selected ? 'bg-indigo-600 text-white shadow-sm'
                                                    : 'text-gray-500',
                                            struck && isPlainDay && (struckDone ? 'text-emerald-500' : 'text-red-400')
                                        )}>{cell.day}</span>
                                    </div>
                                    <div className="mt-2 hidden space-y-1 lg:block">
                                        {rows.slice(0, 3).map(item => {
                                            const isStage = item.trainingMode === 'stage';
                                            const anchor = isStage ? stageAnchors.get(item.id) : undefined;
                                            const locked = isStage && anchor?.status === 'locked';
                                            const completed = isStage ? anchor?.status === 'completed' : Boolean(item.progress?.completedAt);
                                            const overdue = isStage && anchor?.status === 'active' && (anchor?.overdueDays || 0) > 0;
                                            const dueNow = overdue && cell.date === todayKey;
                                            const palette = locked ? 'bg-gray-100 text-gray-400 ring-gray-200'
                                                : completed ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                                                    : overdue ? 'bg-red-50 text-red-600 ring-red-200'
                                                        : isStage ? 'bg-violet-50 text-violet-700 ring-violet-200'
                                                            : 'bg-sky-50 text-sky-700 ring-sky-200';
                                            const dot = locked ? 'bg-gray-300' : completed ? 'bg-emerald-500' : overdue ? 'bg-red-400' : isStage ? 'bg-violet-500' : 'bg-sky-500';
                                            return (
                                                <span key={item.id} className={cx('flex w-full items-center gap-1.5 truncate rounded-md px-1.5 py-1 text-[10px] font-medium ring-1', palette)}>
                                                    {locked
                                                        ? <Lock className="h-2.5 w-2.5 shrink-0" />
                                                        : completed
                                                            ? <CheckCircle2 className="h-3 w-3 shrink-0" />
                                                            : <span className={cx('h-1.5 w-1.5 shrink-0 rounded-full', dot)} />}
                                                    <span className={cx('truncate', locked && 'italic')}>{dueNow ? 'Due now · ' : ''}{item.title}</span>
                                                </span>
                                            );
                                        })}
                                        {rows.length > 3 && <div className="text-[10px] text-gray-400">+{rows.length - 3}</div>}
                                    </div>
                                    {rows.length > 0 && (
                                        <div className="mt-2 flex gap-1 lg:hidden">
                                            {rows.slice(0, 5).map(item => {
                                                const isStage = item.trainingMode === 'stage';
                                                const anchor = isStage ? stageAnchors.get(item.id) : undefined;
                                                const cls = isStage
                                                    ? (anchor?.status === 'locked' ? 'bg-gray-300' : anchor?.status === 'completed' ? 'bg-emerald-500' : (anchor?.overdueDays || 0) > 0 ? 'bg-red-400' : 'bg-violet-500')
                                                    : (item.progress?.completedAt ? 'bg-emerald-500' : 'bg-sky-500');
                                                return <span key={item.id} className={cx('h-1.5 w-1.5 rounded-full', cls)} />;
                                            })}
                                        </div>
                                    )}
                                </>
                            )}
                        </button>
                    );
                })}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t bg-gray-50/60 px-3 py-2 text-[11px] text-gray-500">
                <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-sky-500" />Scheduled</span>
                <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-violet-500" />Stage</span>
                <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-red-400" />Overdue</span>
                <span className="inline-flex items-center gap-1.5"><Lock className="h-2.5 w-2.5 text-gray-400" />Locked (projected)</span>
            </div>
        </div>
    );
};

const SelectedTrainingDateList = ({
    date,
    trainings,
    allTrainings,
    now,
    onOpen,
}: {
    date: string;
    trainings: TrainingItem[];
    allTrainings: TrainingItem[];
    now: Date;
    onOpen: (item: TrainingItem) => void;
}) => {
    const parsed = parsePickerDate(date);
    return (
        <div className="bg-white">
            <div className="flex flex-col gap-1 border-b px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h3 className="text-sm font-semibold text-gray-950">{parsed ? format(parsed, 'MMMM d, yyyy') : date}</h3>
                    <p className="text-xs text-gray-500">Training scheduled or unlocked on this date.</p>
                </div>
                <Badge variant="outline">{trainings.length} item{trainings.length === 1 ? '' : 's'}</Badge>
            </div>
            <div className="divide-y">
                {trainings.length === 0 && (
                    <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-sm text-gray-500">
                        <CalendarDays className="h-8 w-8 text-gray-300" />
                        No training on this date.
                    </div>
                )}
                {trainings.map(item => {
                    const itemIndex = allTrainings.findIndex(row => row.id === item.id);
                    return <TrainingSummaryRow key={item.id} item={item} index={itemIndex} items={allTrainings} now={now} onOpen={onOpen} />;
                })}
            </div>
        </div>
    );
};

const TrainingSummaryRow = ({ item, index, items, now, onOpen }: { item: TrainingItem; index: number; items: TrainingItem[]; now: Date; onOpen: (item: TrainingItem) => void }) => {
    const state = trainingDisplayState(item, index, items, now);
    const catMeta = categoryChip(item.trainingType || item.category);
    return (
        <button
            type="button"
            disabled={state.locked}
            onClick={() => !state.locked && onOpen(item)}
            className={cx('flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50', state.locked && 'cursor-not-allowed opacity-60')}
        >
            <div className={cx('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-semibold', state.locked ? 'bg-gray-100 text-gray-400' : 'bg-gray-100 text-gray-700')}>
                {state.locked ? <Lock className="h-4 w-4" /> : Math.max(index + 1, 1)}
            </div>
            <div className="min-w-0 flex-1">
                <div className={cx('font-medium text-gray-950', state.procrastinationStyle?.text)}>{item.title}</div>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    {state.procrastinationDays > 0 ? (
                        <>
                            <span className="font-medium text-red-600">Due now</span>
                            {item.availableFrom && <span>Since {formatStageDate(item.availableFrom)}</span>}
                        </>
                    ) : (
                        <span>{trainingTimeLabel(item) || formatTrainingStart(item)}</span>
                    )}
                    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium ${catMeta.className}`}>{catMeta.label}</span>
                    {state.isStage && <Badge variant="outline">Stage</Badge>}
                    {state.isStage && state.completed && (item.completedDate || item.progress?.completedAt) && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                            <CheckCircle2 className="h-3 w-3" />Completed {formatStageDate(item.completedDate || item.progress?.completedAt)}
                        </span>
                    )}
                    {state.procrastinationStyle && (
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${state.procrastinationStyle.badge}`}>
                            <AlertTriangle className="h-3 w-3" />
                            {state.procrastinationDays} day{state.procrastinationDays === 1 ? '' : 's'} overdue
                        </span>
                    )}
                </div>
            </div>
            {state.locked ? (
                <Badge variant="outline" className="gap-1 text-muted-foreground"><Lock className="h-3 w-3" />Locked</Badge>
            ) : (
                <Badge variant={state.statusVariant}>{state.status}</Badge>
            )}
        </button>
    );
};

const ReminderChannels = ({
    value,
    onChange,
    webPushSupported,
    webPushGranted,
}: {
    value: Channels & { repeat?: string; repeatUntilOff?: boolean };
    onChange: (value: any) => void;
    webPushSupported: boolean;
    webPushGranted: boolean;
}) => {
    return (
        <div className="rounded-md border p-3">
            <div className="mb-3 text-sm font-medium">Reminder channels</div>
            <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                        checked={Boolean(value.notifyInApp)}
                        onCheckedChange={checked => onChange({ ...value, notifyInApp: checked ? 1 : 0 })}
                    />
                    In-app notification
                </label>
                <label className="flex flex-wrap items-center gap-2 text-sm">
                    <Checkbox
                        checked={Boolean(value.notifyWebPush)}
                        onCheckedChange={checked => onChange({ ...value, notifyWebPush: checked ? 1 : 0 })}
                    />
                    Web push notification
                    {!webPushGranted && (
                        <span className="text-xs text-amber-600">
                            {webPushSupported ? 'Remember to allow notifications in your profile to receive these.' : '(not supported on this device)'}
                        </span>
                    )}
                </label>
            </div>
        </div>
    );
};

const QuickReminder = ({ lead, disabled, onSet }: { lead: Lead; disabled?: boolean; onSet: (lead: Lead, days: number) => Promise<void> | void }) => {
    const [open, setOpen] = useState(false);
    const [days, setDays] = useState('1');
    const [busy, setBusy] = useState(false);
    const hasReminder = Boolean(lead.reminderAt);
    const reminderLabel = lead.reminderAt ? new Date(`${String(lead.reminderAt).slice(0, 10)}T00:00:00`).toLocaleDateString() : '';
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button type="button" size="icon" variant={hasReminder ? 'default' : 'outline'} className="w-8" disabled={disabled} title={hasReminder ? `Reminder set for ${reminderLabel}` : 'Set a reminder'}>
                    {hasReminder ? <BellRing className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56" align="end">
                <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-900">Remind me in</div>
                    {hasReminder && <p className="text-[11px] text-emerald-600">Reminder already set for {reminderLabel}</p>}
                    <div className="flex items-center gap-2">
                        <Input type="number" min={0} max={60} value={days} onChange={e => setDays(e.target.value)} className="h-8 w-20" />
                        <span className="text-sm text-gray-500">days</span>
                    </div>
                    <Button type="button" size="sm" className="w-full" disabled={busy} onClick={async () => { setBusy(true); await onSet(lead, Number(days)); setBusy(false); setOpen(false); }}>
                        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Bell className="mr-2 h-4 w-4" />}
                        Set reminder
                    </Button>
                    <p className="text-[10px] text-gray-400">You'll get an in-app + push notification when due.</p>
                </div>
            </PopoverContent>
        </Popover>
    );
};

const InlineLeadNotes = ({ lead, onSave }: { lead: Lead; onSave: (lead: Lead, notes: string) => Promise<void> }) => {
    const [value, setValue] = useState(lead.notes || '');
    const timer = useRef<number | undefined>(undefined);
    const lastSaved = useRef(lead.notes || '');

    // Reset when a different lead lands in this cell.
    useEffect(() => {
        setValue(lead.notes || '');
        lastSaved.current = lead.notes || '';
    }, [lead.id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

    const scheduleSave = (next: string) => {
        setValue(next);
        if (timer.current) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(async () => {
            if (next === lastSaved.current) return;
            try {
                await onSave(lead, next);
                lastSaved.current = next;
                toast.success('Remarks saved');
            } catch {
                toast.error('Could not save remarks');
            }
        }, 700);
    };

    return (
        <Textarea
            rows={1}
            value={value}
            onChange={e => scheduleSave(e.target.value)}
            placeholder="Add remarks… (saved automatically)"
            className="min-h-9 w-[280px] resize-y text-xs"
        />
    );
};

const LeadStatusBadge = ({ status }: { status?: string | null }) => {
    const value = status || 'draft';
    // Live openings get a clear green badge.
    if (value === 'live' || value === 'available') {
        return <Badge className="border-emerald-200 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{value.replace(/_/g, ' ')}</Badge>;
    }
    const variant = value === 'closed' ? 'secondary' : 'outline';
    return <Badge variant={variant as any}>{value.replace(/_/g, ' ')}</Badge>;
};

const leadTemperatures = [
    { value: 'cold', label: 'Cold', dot: 'bg-sky-500', badge: 'bg-sky-50 text-sky-700 ring-1 ring-sky-200' },
    { value: 'warm', label: 'Warm', dot: 'bg-amber-500', badge: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' },
    { value: 'hot', label: 'Hot', dot: 'bg-rose-500', badge: 'bg-rose-50 text-rose-700 ring-1 ring-rose-200' },
];
const leadTemperatureMeta = (value?: string | null) => leadTemperatures.find(item => item.value === value) || null;

const LeadTemperatureBadge = ({ value }: { value?: string | null }) => {
    const meta = leadTemperatureMeta(value);
    if (!meta) return <span className="text-xs text-gray-400">—</span>;
    return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${meta.badge}`}><span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />{meta.label}</span>;
};

const LeadTemperaturePicker = ({ value, disabled, onChange }: { value?: string | null; disabled?: boolean; onChange: (value: string) => void }) => {
    const meta = leadTemperatureMeta(value);
    return (
        <Select value={value || 'none'} disabled={disabled} onValueChange={next => onChange(next === 'none' ? '' : next)}>
            <SelectTrigger className="h-8 w-full text-xs">
                <span className="flex items-center gap-1.5">
                    {meta ? <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} /> : null}
                    <span className={meta ? '' : 'text-gray-400'}>{meta?.label || 'Set'}</span>
                </span>
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="none" className="text-xs text-gray-400">None</SelectItem>
                {leadTemperatures.map(temp => (
                    <SelectItem key={temp.value} value={temp.value} className="text-xs">
                        <span className="flex items-center gap-1.5"><span className={`h-1.5 w-1.5 rounded-full ${temp.dot}`} />{temp.label}</span>
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
};

export default AgentHandbook;
